import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { ShoppingCart, X, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

export const StickyCartBar = () => {
  const { cart, cartCount, cartTotal } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  // Don't show on cart, checkout, or order success pages
  const hiddenPaths = ['/cart', '/checkout', '/order-success', '/admin'];
  const shouldHide = hiddenPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    // Show bar 2 seconds after cart has items (not immediately — feels less pushy)
    if (cartCount > 0 && !shouldHide && !dismissed) {
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
    setShow(false);
  }, [cartCount, shouldHide, dismissed]);

  // Reset dismissed state when cart changes
  useEffect(() => {
    if (cartCount > 0) setDismissed(false);
  }, [cartCount]);

  if (!show || cartCount === 0) return null;

  const firstItem = cart[0];
  const itemText = cartCount === 1
    ? firstItem?.title?.slice(0, 25) + (firstItem?.title?.length > 25 ? '...' : '')
    : `${cartCount} items`;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 lg:bottom-4 lg:left-auto lg:right-4 lg:max-w-sm animate-in slide-in-from-bottom duration-300"
      data-testid="sticky-cart-bar"
    >
      <div className="bg-gray-900 text-white mx-2 mb-2 lg:mx-0 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
        <div className="flex items-center gap-3 p-3">
          {/* Cart icon with count badge */}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {cartCount}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{itemText}</p>
            <p className="text-xs text-gray-400">
              Total: <span className="text-green-400 font-bold">₹{cartTotal.toLocaleString()}</span>
              {' '}&middot; Free delivery
            </p>
          </div>

          {/* CTA */}
          <Button
            size="sm"
            className="flex-shrink-0 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold px-4 h-9"
            onClick={() => navigate('/checkout')}
            data-testid="sticky-cart-checkout-btn"
          >
            Checkout
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>

          {/* Dismiss */}
          <button
            onClick={(e) => { e.stopPropagation(); setDismissed(true); setShow(false); }}
            className="flex-shrink-0 text-gray-500 hover:text-white p-1"
            data-testid="sticky-cart-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
